import { useMemo } from "react";
import useSWR from "swr";

import { APIError } from "@/data/api";
import { toRecurringAvailability } from "@/offering/recurrence";

export interface RecurringAvailabilityRequest {
  durationUnit: "hour" | "day";
  // HHMM, hourly only. Daily has no window to carry.
  startTime?: string;
  endTime?: string;
  quantity?: number;
}

// Every date in the recurrence horizon with a verdict for one fixed request.
// Fetched once when the customer turns Repeat on; changing the preset, the
// count or the anchor date re-filters this list locally, and only a new time
// window (a new key) refetches. There is no background revalidation: the
// verdict is re-read when the window changes and re-checked by the quote and
// the submit, which is where a date lost meanwhile is caught.
//
// A 422 is the room refusing to offer recurring at all — the same gates the
// booking policy's `recurring_eligible` reports — so callers hide the control
// rather than show an error, and nothing retries it.
export function useRecurringAvailabilityQuery(
  spaceId: string | undefined,
  request: RecurringAvailabilityRequest | undefined
) {
  const key = spaceId && request ? buildKey(spaceId, request) : null;

  const response = useSWR(key, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  });

  const error = response.error as APIError | undefined;
  const ineligible = error?.status === 422;

  // Marshalled once per payload: everything downstream memoises on this
  // object's identity, and a fresh one per render would re-expand the rule and
  // rebuild the quote input on every paint.
  const data = useMemo(
    () => (response.data ? toRecurringAvailability(response.data) : undefined),
    [response.data]
  );

  return {
    data,
    loading: !!key && !response.data && !response.error,
    error: ineligible ? undefined : error,
    ineligible,
  };
}

function buildKey(spaceId: string, request: RecurringAvailabilityRequest) {
  if (
    request.durationUnit === "hour" &&
    !(request.startTime && request.endTime)
  ) {
    return null;
  }
  const params = new URLSearchParams();
  params.set("duration_unit", request.durationUnit);
  if (request.durationUnit === "hour") {
    params.set("start_time", request.startTime!);
    params.set("end_time", request.endTime!);
  }
  if (request.quantity && request.quantity > 1) {
    params.set("quantity", String(request.quantity));
  }
  return `/api/v2/spaces/${spaceId}/recurring_availability?${params.toString()}`;
}
